import { track } from '@tldraw/tldraw'
import { useEffect } from 'react'
import Styles from './Caption.module.scss'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { Button } from 'blaze-ui/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { channelSupportsCaption } from 'components/Design/utils/channelSupportsCaption'
import { DesignChannel } from 'components/NewFileModal/Types'
import { AI } from 'blaze-ui/icons'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { InstagramProfile } from 'api/clients/ExternalIntegrationsClient'
import { CaptionDrawer } from 'components/Design/CaptionTab/CaptionDrawer'

interface PreviewProps {
  channel: DesignChannel
  caption: string
  scheduledPostId?: number
}

const Preview = ({ channel, caption, scheduledPostId }: PreviewProps) => {
  const scheduledPostIntegration = useScheduledPost({ id: scheduledPostId }).data?.external_integration

  if ([DesignChannel.InstagramPost, DesignChannel.InstagramReel, DesignChannel.InstagramStory].includes(channel)) {
    let accountName = 'account-not-connected'

    if (scheduledPostIntegration) {
      accountName =
        (scheduledPostIntegration.additional_data as InstagramProfile)?.username || scheduledPostIntegration.name
    }

    return (
      <div className={Styles.preview}>
        <span className={Styles.accountName}>{accountName}</span> {caption}
      </div>
    )
  }

  return <div className={Styles.preview}>{caption}</div>
}

export const Caption = track(
  observer(({ isHovered }: { isHovered: boolean }) => {
    const { files, designStore, drawersStore } = useRootStore()
    const { isMobile } = useResponsiveDesign()
    const captionsStore = designStore?.captionsStore
    const fileId = designStore?.fileId
    const caption = captionsStore?.caption
    const file = designStore?.file

    useEffect(
      function fetchFile() {
        if (!fileId) return
        files.fetch(fileId)
      },
      [files, fileId],
    )

    const openCaptionTab = () => {
      if (isMobile) {
        drawersStore?.openDrawer(CaptionDrawer)
      } else {
        designStore?.openTab('layers')
        requestAnimationFrame(() => {
          designStore?.openTab('caption')
        })
      }
    }

    if (!file) return null

    const supportsCaption = channelSupportsCaption(file.channel as DesignChannel)

    if (!supportsCaption) return null

    if (caption) {
      return (
        <>
          <Preview caption={caption} channel={file.channel as DesignChannel} scheduledPostId={file.scheduled_post_id} />
          {!isMobile && isHovered && (
            <div className={Styles.editButton}>
              <Button size="xs" onPress={openCaptionTab} variant="secondary">
                Edit Caption
              </Button>
            </div>
          )}
        </>
      )
    }

    return (
      <div className={Styles.generateCaption}>
        <Button frontIcon={AI} variant="secondary" size="sm" onPress={openCaptionTab}>
          Generate Caption
        </Button>
      </div>
    )
  }),
)
